import { RegistryCalendar } from 'app/src/features/UserSelfRegistration/components/RegistryCalendar'
import { RegistryTextInput } from 'app/src/features/UserSelfRegistration/components/RegistryTextInput'
import { RegistryItem } from 'app/src/model'

import { useUserSelfRegistrationConfig } from 'app/src/services/welfareportal/membership-api/membership-components'
import type * as Schemas from 'app/src/services/welfareportal/membership-api/membership-schemas'
import { AutocompleteDropdown, ReadableAttachmentList } from 'app/src/shared/components'
import { validateFiscalCode } from 'app/src/shared/helpers/funcutils'
import { getIn, useFormik } from 'formik'
import { DateTime } from 'luxon'
import { Checkbox } from 'primereact/checkbox'
import { SelectItemOptionsType } from 'primereact/selectitem'
import { classNames } from 'primereact/utils'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

interface RegistryFormProps {
  onValidatingForm: (isValid: boolean, values?: Schemas.UserSelfRegistration) => void
}

export function RegistryForm(props: RegistryFormProps) {
  const { t } = useTranslation('selfRegistration', { keyPrefix: 'SelfRegistration' })
  const { data: selfRegConfig } = useUserSelfRegistrationConfig({}, {})
  const { onValidatingForm } = props
  const maxLengthNameFields = 100

  const initialFormData: Partial<RegistryItem> = {
    corporateEmail: undefined,
    taxCode: undefined,
    lastName: undefined,
    firstName: undefined,
    gender: undefined,
    birthDate: undefined,
    privacyAccepted: false,
    healthDataProcessingAccepted: false,
    responsibilityDeclarationAccepted: false,
    privacyDocumentRead: false,
  }

  const genderOptions: SelectItemOptionsType = useMemo(
    () => [
      { value: 'M', label: t('RegistryForm.RegistryData.Gender.Data.M'), key: 'M' },
      { value: 'F', label: t('RegistryForm.RegistryData.Gender.Data.F'), key: 'F' },
    ],
    [t],
  )

  const validateCfLocal = useCallback(
    (fiscalCode: string, name?: string, surname?: string, birthDate?: string, gender?: string) => {
      // Use local validation if we have first and last name, otherwise fallback to API
      return validateFiscalCode(fiscalCode, name, surname, birthDate, gender)
    },
    [],
  )

  const validationSchema = Yup.object({
    corporateEmail: Yup.string()
      .matches(
        /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-zA-Z]{2,6}(?:\.[a-zA-Z]{2})?)$/,
        t('Errors.InvalidEmail'),
      )
      .required(t('Errors.Required', { FIELD: t('RegistryForm.CompanyData.CorporateEmail') })),

    lastName: Yup.string()
      .matches(/^[a-zA-ZÀ-ÿ''\- ]+$/, t('Errors.NameInvalid', { FIELD: t('RegistryForm.RegistryData.LastName') }))
      .required(t('Errors.Required', { FIELD: t('RegistryForm.RegistryData.LastName') }))
      .test(
        'maxLength',
        t('Errors.MaxLengthField', {
          FIELD: t('RegistryForm.RegistryData.LastName'),
          MAX_VALUE: maxLengthNameFields,
        }),
        (value) => {
          if (value && value.length > maxLengthNameFields) {
            return false
          }
          return true
        },
      ),

    firstName: Yup.string()
      .matches(/^[a-zA-ZÀ-ÿ''\- ]+$/, t('Errors.NameInvalid', { FIELD: t('RegistryForm.RegistryData.FirstName') }))
      .required(t('Errors.Required', { FIELD: t('RegistryForm.RegistryData.FirstName') }))
      .test(
        'maxLength',
        t('Errors.MaxLengthField', {
          FIELD: t('RegistryForm.RegistryData.FirstName'),
          MAX_VALUE: maxLengthNameFields,
        }),
        (value) => {
          if (value && value.length > maxLengthNameFields) {
            return false
          }
          return true
        },
      ),

    gender: Yup.string()
      .matches(/^[MF]$/, t('Errors.Invalid'))
      .required(t('Errors.Required', { FIELD: t('RegistryForm.RegistryData.Gender.Label') })),

    birthDate: Yup.string()
      .matches(/^\d{4}-\d{2}-\d{2}$/, t('Errors.Invalid'))
      .required(t('Errors.Required', { FIELD: t('RegistryForm.RegistryData.BirthDate.Label') })),

    taxCode: Yup.string()
      .matches(/^[A-Z0-9]{16}$/, t('Errors.TaxCodeInvalid'))
      .required(t('Errors.Required', { FIELD: t('RegistryForm.RegistryData.TaxCode') }))
      .test({
        async test(value, yupContext) {
          const valueUpper = value?.toUpperCase() ?? ''
          if (valueUpper.length >= 16 && yupContext) {
            // For validation we can't access form values here, so just do basic CF validation
            const validateResponse = validateCfLocal(
              valueUpper,
              yupContext.parent.firstName,
              yupContext.parent.lastName,
              yupContext.parent.birthDate,
              yupContext.parent.gender,
            )

            if (!validateResponse.isValid) return yupContext.createError({ message: t('Errors.TaxCodeDataMismatch') })
          }

          return true
        },
      }),

    privacyAccepted: Yup.boolean()
      .oneOf([true], t('RegistryForm.TermsAndConditions.PrivacyRequiredText'))
      .required(t('RegistryForm.TermsAndConditions.PrivacyRequiredText')),

    privacyDocumentRead: Yup.boolean()
      .oneOf([true], t('RegistryForm.TermsAndConditions.PrivacyDocumentRequiredText'))
      .required(t('RegistryForm.TermsAndConditions.PrivacyDocumentRequiredText')),

    responsibilityDeclarationAccepted: Yup.boolean()
      .oneOf([true], t('RegistryForm.TermsAndConditions.IdentityRequiredText'))
      .required(t('RegistryForm.TermsAndConditions.IdentityRequiredText')),
  })

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      console.log('Dati inviati:', values)
    },
  })

  const isFormFieldValid = (field: string | string[]) => !!(getIn(formik.touched, field) && getIn(formik.errors, field))

  const getFormErrorMessage = (field: keyof Partial<RegistryItem>) => {
    return (
      isFormFieldValid(field) && (
        <p className='p-error text-justify text-sm' data-testid={`${field}ErrorLabel`}>
          {getIn(formik.errors, field)}
        </p>
      )
    )
  }

  const { values, validateForm } = formik

  useEffect(() => {
    const checkValidity = async () => {
      const errors = await validateForm()
      return Object.keys(errors).length === 0
    }

    checkValidity().then((isValid) => {
      if (!isValid) {
        onValidatingForm(false)
      } else {
        onValidatingForm(true, {
          userEmail: values.corporateEmail,
          firstName: values.firstName,
          lastName: values.lastName,
          userBirthDate: values.birthDate,
          fiscalCode: values.taxCode,
          userGender: values.gender,
        } as Schemas.UserSelfRegistration)
      }
    })
  }, [values, validateForm, onValidatingForm])

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='flex flex-col gap-6 p-2'>
        <div className='flex flex-col gap-4'>
          <p className='font-semibold'>{t('RegistryForm.RegistryData.Title')}</p>
          <div className='mt-6 grid gap-12 lg:grid-cols-2 lg:gap-20'>
            <div>
              <RegistryTextInput
                id='firstName'
                name='firstName'
                data-testid='firstName'
                value={formik.values.firstName ?? ''}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  formik.handleBlur(e)
                  formik.setFieldValue('firstName', e.target.value.trim())
                }}
                className={'w-full capitalize' + classNames({ 'p-invalid': isFormFieldValid('firstName') })}
                labelProps={{
                  text: t('RegistryForm.RegistryData.FirstName'),
                  labelStyle: undefined,
                }}
              />
              {getFormErrorMessage('firstName')}
            </div>
            <div>
              <RegistryTextInput
                id='lastName'
                name='lastName'
                data-testid='lastName'
                value={formik.values.lastName ?? ''}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  formik.handleBlur(e)
                  formik.setFieldValue('lastName', e.target.value.trim())
                }}
                className={'w-full ' + classNames({ 'p-invalid': isFormFieldValid('lastName') })}
                labelProps={{
                  text: t('RegistryForm.RegistryData.LastName'),
                  labelStyle: undefined,
                }}
              />
              {getFormErrorMessage('lastName')}
            </div>
            <div>
              <RegistryCalendar
                id='birthDate'
                name='birthDate'
                data-testid='birthDate'
                maxDate={new Date()}
                value={formik.values.birthDate ? new Date(formik.values.birthDate) : null}
                onChange={(e) => formik.setFieldValue('birthDate', DateTime.fromJSDate(e.value!).toFormat('yyyy-MM-dd'))}
                onBlur={formik.handleBlur}
                className={'w-full ' + classNames({ 'p-invalid': isFormFieldValid('birthDate') })}
                labelProps={{
                  text: t('RegistryForm.RegistryData.BirthDate.Label'),
                  labelStyle: undefined,
                }}
              />
              {getFormErrorMessage('birthDate')}
            </div>
            <div>
              <AutocompleteDropdown
                id='gender'
                name='gender'
                options={genderOptions}
                value={formik.values.gender}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={'w-full ' + classNames({ 'p-invalid': isFormFieldValid('gender') })}
                aria-label={t('RegistryForm.RegistryData.Gender.Label')}
                labelProps={{
                  text: t('RegistryForm.RegistryData.Gender.Label'),
                  labelStyle: undefined,
                  testId: 'gender',
                }}
              />
              {getFormErrorMessage('gender')}
            </div>
            <div>
              <RegistryTextInput
                id='taxCode'
                name='taxCode'
                data-testid='taxCode'
                value={formik.values.taxCode ?? ''}
                onChange={(e) => {
                  formik.handleChange(e)
                  formik.setFieldValue('taxCode', e.target.value.toUpperCase().trim())
                }}
                onBlur={formik.handleBlur}
                className={'w-full ' + classNames({ 'p-invalid': isFormFieldValid('taxCode') })}
                labelProps={{
                  text: t('RegistryForm.RegistryData.TaxCode'),
                  labelStyle: undefined,
                }}
              />
              {getFormErrorMessage('taxCode')}
            </div>
          </div>
        </div>
        <div></div>
        <div className='flex flex-col gap-4'>
          <p className='font-semibold'>{t('RegistryForm.CompanyData.Title')}</p>
          <div className='mt-6 grid gap-20 lg:grid-cols-2'>
            <div>
              <RegistryTextInput
                id='corporateEmail'
                name='corporateEmail'
                data-testid='corporateEmail'
                value={formik.values.corporateEmail ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={'w-full ' + classNames({ 'p-invalid': isFormFieldValid('corporateEmail') })}
                labelProps={{
                  text: t('RegistryForm.CompanyData.CorporateEmail'),
                  labelStyle: undefined,
                }}
              />
              {getFormErrorMessage('corporateEmail')}
            </div>
            <div></div>
          </div>
        </div>
        <div className='flex flex-col gap-4'>
          <p className='font-semibold'>{t('RegistryForm.TermsAndConditions.Title')}</p>
          <div className='mt-4 flex flex-col gap-4'>
            <div className='flex items-start gap-3'>
              <Checkbox
                id='privacyAccepted'
                name='privacyAccepted'
                data-testid='privacyAccepted'
                checked={formik.values.privacyAccepted ?? false}
                onChange={(e) => formik.setFieldValue('privacyAccepted', e.checked)}
                onBlur={formik.handleBlur}
                className={classNames({ 'p-invalid': isFormFieldValid('privacyAccepted') })}
                aria-label={t('RegistryForm.TermsAndConditions.PrivacyText')}
              />
              <label htmlFor='privacyAccepted' className='text-sm'>
                {t('RegistryForm.TermsAndConditions.PrivacyText')} *
              </label>
            </div>
            {getFormErrorMessage('privacyAccepted')}

            <div className='flex flex-col items-start'>
              <ReadableAttachmentList
                attachments={
                  selfRegConfig?.privacyPolicyUrl
                    ? [
                        {
                          id: '1',
                          name: 'Informativa privacy',
                          resourceLink: selfRegConfig.privacyPolicyUrl,
                          type: 'privacy',
                        },
                      ]
                    : []
                }
                title={''}
                onAttachmentRead={(_, attachmentName) => {
                  console.log('Documento privacy letto:', attachmentName)
                  formik.setFieldValue('privacyDocumentRead', true)
                }}
              />
              {!formik.values.privacyDocumentRead && (
                <p className='text-error'>{t('RegistryForm.TermsAndConditions.PrivacyDocumentRequiredText')}</p>
              )}
            </div>
            <div className='flex items-start gap-3'>
              <Checkbox
                id='responsibilityDeclarationAccepted'
                name='responsibilityDeclarationAccepted'
                data-testid='responsibilityDeclarationAccepted'
                checked={formik.values.responsibilityDeclarationAccepted ?? false}
                onChange={(e) => formik.setFieldValue('responsibilityDeclarationAccepted', e.checked)}
                onBlur={formik.handleBlur}
                className={classNames({ 'p-invalid': isFormFieldValid('responsibilityDeclarationAccepted') })}
                aria-label={t('RegistryForm.TermsAndConditions.IdentityText')}
              />
              <label htmlFor='responsibilityDeclarationAccepted' className='text-sm'>
                {t('RegistryForm.TermsAndConditions.IdentityText')} *
              </label>
            </div>
            {getFormErrorMessage('responsibilityDeclarationAccepted')}
          </div>
        </div>
      </div>
    </form>
  )
}
